import { Toaster } from '@/components/UI/Toast/Toast';
import {
  EventType,
  InteractionType,
  PublicClientApplication,
  RedirectRequest
} from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import axios from 'axios';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import React, { useEffect, useLayoutEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
  useNavigate
} from 'react-router';

import '@fontsource-variable/hanken-grotesk';
import '@fontsource-variable/jetbrains-mono';
import '@fontsource-variable/montserrat';
import './App.css';

import { b2cPolicies, domainHintRequest, loginRequest, msalConfig } from './authConfig';
import LoadingBackdrop from './components/Backdrop/LoadingBackdrop';
import { SupportModeProvider } from './contexts/SupportModeContext';
import { getPlaygroundModeHeaders } from './http/Helpers/playgroundModeHeader';
import { ErrorBoundaryFallback } from './pages/NewUI/ErrorBoundary';
import { Footer } from './pages/NewUI/Footer';
import MinimalNavigationHeader from './pages/NewUI/MinimalNavigationHeader';
import NavigationHeader from './pages/NewUI/NavigationHeader';
import PlaygroundModeBanner from './pages/NewUI/PlaygroundModeBanner';
import PlaygroundRailLayout from './pages/NewUI/PlaygroundRailLayout';
import PlaygroundRoleSwitcher from './pages/NewUI/PlaygroundRoleSwitcher';
import StickyAppChrome from './pages/NewUI/StickyAppChrome';
import { getOnboardingLayoutFlag } from './pages/NewUI/SupplierOnboardingRequest/components/useOnboardingLayoutToggle';
import { useFrontendFeature } from './util/functions/frontendFeatureToggles';
import queryKeys from './util/hooks/queries/queryKeys';
import useCompanyRegistrationInfo from './util/hooks/queries/useCompanyRegistrationInfo';
import NotificationListener from './util/notifications/notificationLister';
import { identifyUserFromAccount, registerSuperProperties } from './util/posthogIdentity';

// ✅ MSAL SAFE INIT (browser only)
/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance: PublicClientApplication | null =
  globalThis.window === undefined ? null : new PublicClientApplication(msalConfig);

const queryClient = new QueryClient();

// Attaches the Playground Mode header (Bugs #8515/#8519) to every axios-based call in one place,
// instead of touching each of httpRequestHelpers.ts's call sites individually — see
// playgroundModeHeader.ts for why this exists and what reads it on the backend.
axios.interceptors.request.use((config) => {
  Object.assign(config.headers, getPlaygroundModeHeaders(config.url));
  return config;
});

// Screens that adopt the stickyTables app-shell (document doesn't scroll; the content
// pane does). Grow this list as screens are verified under the PoC toggle.
const STICKY_TABLES_SHELL_PATHS = [
  '/agent-workcentre',
  '/datamanagment-partners',
  '/myoffice/supplier-onboarding-dashboard',
  '/network',
  '/virtual-marketplace'
];

// Safe account setup (browser only)
if (globalThis.window !== undefined) {
  const accounts = msalInstance.getAllAccounts();

  if (accounts.length === 1) {
    msalInstance.setActiveAccount(accounts[0]);
  } else if (accounts.length > 1) {
    // Multiple accounts found - this shouldn't happen in normal flow
    // Clear all but keep none as active to force fresh login
    console.warn('Multiple MSAL accounts found in cache, clearing stale accounts');
    msalInstance.setActiveAccount(null);
  }
}

/** Renders children only after mount so prerender + first hydrate pass match (null). Avoids RQ Devtools / react-hot-toast DOM mismatches. */
function ClientOnly({ children }: { readonly children: React.ReactNode }) {
  const [ready, setReady] = React.useState(false);
  useEffect(() => {
    setReady(true);
  }, []);
  if (!ready) return null;
  return <>{children}</>;
}

export function Layout({ children }: { readonly children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <Meta />
        <Links />
      </head>
      <body>
        <ClientOnly>
          <Toaster position="top-right" />
        </ClientOnly>

        <QueryClientProvider client={queryClient}>
          <NuqsAdapter>{children}</NuqsAdapter>

          {(import.meta.env.DEV ||
            import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === 'true') && (
            <ClientOnly>
              <ReactQueryDevtools initialIsOpen={false} />
            </ClientOnly>
          )}
        </QueryClientProvider>

        <ClientOnly>
          <ScrollRestoration getKey={(location) => location.pathname} />
        </ClientOnly>

        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const companyRegistrationStatus = useCompanyRegistrationInfo();
  // stickyTables PoC toggle — subscribed so the in-app switch re-renders the shell live.
  // Server snapshot is false, so the first client paint matches prerender (no hydration risk).
  const { enabled: stickyTablesEnabled } = useFrontendFeature('stickyTables');

  // First paint must match SPA prerender (null). useLayoutEffect + setState re-enters during the hydrate commit and can trigger React "Root did not complete" (e.g. with discrete events); useEffect defers the flip until after passive effects.
  const [clientMounted, setClientMounted] = React.useState(false);
  useEffect(() => {
    setClientMounted(true);
  }, []);

  /**
   * Using the event API, you can register an event callback that will do something when an event is emitted.
   * When registering an event callback in a react component you will need to make sure you do 2 things.
   * 1) The callback is registered only once
   * 2) The callback is unregistered before the component unmounts.
   * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/events.md
   */

  // Paths that indicate user has exited registration flow (only /viewprofile/* and /myoffice/*
  // are reached AFTER registration completes, so we must not redirect from these paths.
  // /dashboard on sign-in should redirect to /registration if incomplete.)
  const isRegistrationExitPath = (path: string): boolean => {
    return path.startsWith('/viewprofile/') || path.startsWith('/myoffice/');
  };

  const needsRegistrationRedirect =
    companyRegistrationStatus.isFetched &&
    !companyRegistrationStatus.data?.registrationCompleted &&
    location.pathname !== '/registration' &&
    !isRegistrationExitPath(location.pathname);

  // Redirect before paint when possible (reduces dashboard flash). Must not block <Outlet /> —
  // hiding the root outlet can prevent the catch-all route from mounting, so navigate() never
  // activates /registration and needsRegistrationRedirect stays true forever (infinite loader).
  useLayoutEffect(() => {
    if (!clientMounted) return;

    if (needsRegistrationRedirect) {
      navigate('/registration', { replace: true });
    }
  }, [clientMounted, needsRegistrationRedirect, navigate]);

  // Register PostHog super properties once + identify any already-active account
  // (covers page reloads where LOGIN_SUCCESS won't fire again).
  useEffect(() => {
    registerSuperProperties();
    const active = msalInstance?.getActiveAccount();
    if (active) identifyUserFromAccount(active);
  }, []);

  //  MSAL event handling
  useEffect(() => {
    const callbackId = msalInstance?.addEventCallback((event: any) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload?.account) {
        msalInstance.setActiveAccount(event.payload.account);
        identifyUserFromAccount(event.payload.account);
        void queryClient.invalidateQueries({
          queryKey: [queryKeys.companyRegistrationInfo]
        });
        void queryClient.invalidateQueries({ queryKey: [queryKeys.baseCompanyInfo] });
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        // This handles the instance where a user clicks the Cancel button on the B2C sign-up/password change/etc pages.
        if (event.error?.errorMessage.includes('AADB2C90091')) {
          msalInstance.loginRedirect(
            b2cPolicies.authorities.signUpSignIn as RedirectRequest
          );
        }

        // This handles the "Forgot Password" processing/routing.
        if (event.error?.errorMessage.includes('AADB2C90118')) {
          if (event.interactionType === InteractionType.Redirect) {
            msalInstance.loginRedirect(
              b2cPolicies.authorities.forgotPassword as RedirectRequest
            );
          } else {
            msalInstance
              .loginPopup(b2cPolicies.authorities.forgotPassword as RedirectRequest)
              .catch((e: any) => {
                throw e;
              });
          }
        }
      }

      // Added this to negate the AADB2C90077 error occassionally experienced.
      if (event.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
        msalInstance.loginRedirect(
          b2cPolicies.authorities.signUpSignIn as RedirectRequest
        );
      }
    });

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, [queryClient]);

  // Full-height layouts that manage their own viewport — hide the global
  // footer and drop min-h-screen so the sidebar layout can pin to the viewport
  // without the wrapper pushing an extra scrollable gap below it.
  const isOnboardingPath =
    location.pathname.startsWith('/myoffice/supplier-onboarding-dashboard/onboarding/') ||
    location.pathname.startsWith('/myoffice/mto/');

  const isInviteExpiredPath = location.pathname === '/invite-expired';

  // BLOCK until after first client paint (hydration-safe null matches HydrateFallback / prerender)
  if (!clientMounted) {
    return null; // or <LoadingBackdrop showBackdrop />
  }

  // After hydration only: reads localStorage — must not run during SPA prerender / first paint.
  const isFullHeightLayout =
    location.pathname.startsWith('/myoffice/configuration') ||
    (isOnboardingPath && getOnboardingLayoutFlag());

  // stickyTables PoC (off by default): app-shell layout for the big table screens —
  // the document never scrolls; the content pane does. Keeps header always visible and
  // lets tables fill the remaining viewport. Distinct from isFullHeightLayout, whose
  // pane is overflow-hidden (those screens manage their own internal scroll).
  const isStickyTablesShell =
    !isFullHeightLayout &&
    stickyTablesEnabled &&
    STICKY_TABLES_SHELL_PATHS.some((path) => location.pathname.startsWith(path));

  // Invite-expired page is shown when B2C redirects with expired id_token_hint (e.g. company invite).
  // Render it without MsalAuthenticationTemplate so we don't redirect unauthenticated users to login.
  if (isInviteExpiredPath) {
    return (
      <PostHogProvider client={posthog}>
        <MsalProvider instance={msalInstance}>
          <MinimalNavigationHeader />
          <div className="h-full min-h-screen">
            <Outlet />
          </div>
        </MsalProvider>
      </PostHogProvider>
    );
  }

  // Keep the outlet mounted on /registration so the stepper can render while status loads.
  // Blocking the root outlet globally breaks child routes and caused infinite loaders.
  const shouldShowLoading =
    companyRegistrationStatus.isLoading && location.pathname !== '/registration';

  const outletContent = shouldShowLoading ? <LoadingBackdrop showBackdrop /> : <Outlet />;

  // PlaygroundModeBanner is rendered as the first child inside each shell wrapper, above
  // NavigationHeader — not as a sibling in front of renderAppShell() — so its height is
  // accounted for by the shell's flex column instead of sitting on top of a full 100dvh/
  // 100vh pane (review, PR #2577, fp:banner-outside-app-shell). Two of the three shells
  // are fixed-height flex columns with their own scrolling pane, not the document, so a
  // sibling banner would both push the pane's bottom off-screen and make its `sticky
  // top-0` a no-op (nothing here scrolls at the document level to stick against).
  const renderAppShell = () => {
    if (isFullHeightLayout) {
      return (
        <div className="flex min-h-dvh flex-col overflow-hidden">
          <PlaygroundModeBanner />
          <PlaygroundRoleSwitcher />
          <NavigationHeader />
          <ErrorBoundary
            resetKeys={[location.key]}
            FallbackComponent={ErrorBoundaryFallback}
          >
            <PlaygroundRailLayout>
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {outletContent}
              </div>
            </PlaygroundRailLayout>
          </ErrorBoundary>
        </div>
      );
    }
    if (isStickyTablesShell) {
      // stickyTables app shell: header pinned, the PANE scrolls (not the document).
      // Unlike isFullHeightLayout the pane is overflow-y-auto, because these are
      // ordinary card pages that rely on an ancestor scroll container. No global
      // Footer here — on table screens it only stole viewport; legal/version meta
      // moves into the table's pagination bar.
      return (
        <div className="flex h-dvh flex-col overflow-hidden">
          <PlaygroundModeBanner />
          <PlaygroundRoleSwitcher />
          <NavigationHeader />
          <ErrorBoundary
            resetKeys={[location.key]}
            FallbackComponent={ErrorBoundaryFallback}
          >
            <PlaygroundRailLayout>
              <div className="sentry-feedback-clearance flex min-h-0 flex-1 flex-col overflow-y-auto">
                {outletContent}
              </div>
            </PlaygroundRailLayout>
          </ErrorBoundary>
        </div>
      );
    }
    return (
      <>
        {/* This shell (unlike the two above) has no wrapping flex column — the document
            itself scrolls — so a Fragment here would leave the banner and NavigationHeader
            as independent document-level siblings, each trying to stick at top:0 on its
            own and racing for the same spot once scrolled (review, PR #2577). Grouping
            them under one sticky container makes them stick together as a single unit;
            NavigationHeader's own `sticky top-0` becomes a no-op inside it, which is
            harmless. */}
        <StickyAppChrome>
          <PlaygroundModeBanner />
          <PlaygroundRoleSwitcher />
          <NavigationHeader />
        </StickyAppChrome>
        <ErrorBoundary
          resetKeys={[location.key]}
          FallbackComponent={ErrorBoundaryFallback}
        >
          <PlaygroundRailLayout scrollsDocument>
            <div className="sentry-feedback-clearance h-full min-h-screen">
              {outletContent}
            </div>
          </PlaygroundRailLayout>
          <Footer />
        </ErrorBoundary>
      </>
    );
  };

  return (
    <PostHogProvider client={posthog}>
      <MsalProvider instance={msalInstance}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          authenticationRequest={
            domainHintRequest.extraQueryParameters ? loginRequest : undefined
          }
        >
          <SupportModeProvider>
            {renderAppShell()}

            <NotificationListener />
          </SupportModeProvider>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    </PostHogProvider>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals();
/* eslint-disable react-refresh/only-export-components -- React Router root module: meta/links/HydrateFallback + msalInstance/queryClient are framework contract, not HMR component exports */
export { msalInstance, queryClient };
// Re-export loader functions from separate file to maintain build compatibility
export { meta, links, HydrateFallback } from './root.loaders';
/* eslint-enable react-refresh/only-export-components */
